import { AuthStore } from "src/stores/AuthStore";
import { Role } from "src/network/User";
import { ConfigSettings } from "./NetworkConfig";
import { User, getRoleLabel } from "./User";
import crypto from "crypto";
import base64url from "base64url";
import { v4 as uuidv4 } from "uuid";

function info(message: string) {
    // console.log(message);
}

interface IGenerateRequestIdResponse {
    success: boolean;
    status: number;
    data: {
        requestId: string;
        state: string;
    };
}

interface ILoginUserResponse {
    success: boolean;
    status: number;
    data: {
        code: string;
        state: string;
        expires_in: number;
        session_state: string;
        viewtype: string;
        grant_type: string;
    };
}

interface IGetTokenResponse {
    access_token: string;
    id_token: string;
    state: string;
    refresh_token: string;
    expires_in: number;
    id_token_expires_in: number;
    token_type: string;
    sub: string;
    sid: string;
    identity_id: string;
}

class AuthError extends Error {
    area: string;
    constructor(message: string, area: string) {
        super(message);
        this.name = "AuthError";
        this.area = area;
    }
}

export class AuthClient {
    private config: ConfigSettings;
    private authStore: AuthStore;

    constructor(config: ConfigSettings, authStore: AuthStore) {
        this.config = config;
        this.authStore = authStore;
    }

    async getECClientCredentialToken(): Promise<string> {
        this.authStore.setLogin(true);

        const response = await fetch(`${this.config.cidaasBaseUrl}${this.config.cidaasGetTokenRessource}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
            },
            body: new URLSearchParams({
                grant_type: "password",
                client_id: this.config.wwEasyContactClientId,
                client_secret: this.config.wwEasyContactClientSecret,
                username: this.config.wwEasyContactClientUsername,
                password: this.config.wwEasyContactClientPassword
            })
        });

        info(`login response: ${response.status}: ${response.statusText}`);

        if (response.ok) {
            const token = await response.json();
            return token.access_token;
        } else {
            return Promise.reject(
                new AuthError(`${response.status}: ${response.statusText}`, "login credential client")
            );
        }
    }

    async handleLoginErrorResponse(fetchResponse: Response): Promise<never> {
        let responseBody: any;

        try {
            responseBody = await fetchResponse.json();
        } catch (error) {
            return Promise.reject(new AuthError(`Error parsing JSON response: ${error}`, "login"));
        }

        if (responseBody?.error) {
            const error = responseBody.error;
            info(`--> ${error.error}: ${error.error_description}`);

            if (error.error) {
                return Promise.reject(new AuthError(`${error.error}`, "login"));
            } else if (error.error_description) {
                return Promise.reject(new AuthError(`${error.error_description}`, "login"));
            }
        }

        // If there is no error key in the response body, reject with the HTTP status
        return Promise.reject(new AuthError(`${fetchResponse.status}: ${fetchResponse.statusText}`, "login"));
    }

    async generateRequestId(loginCodeVerifier: string): Promise<IGenerateRequestIdResponse> {
        info("generateRequestId starts");

        const hash = crypto.createHash("sha256").update(loginCodeVerifier).digest();
        const loginCodeChallenge = base64url.encode(hash);

        const state = uuidv4();
        const nonce = uuidv4();

        const generateRequestIdResponse = await fetch(
            `${this.config.cidaasBaseUrl}${this.config.cidaasGenerateRequestIdRessource}`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
                },
                body: new URLSearchParams({
                    client_id: this.config.wwEasyContactPasswordClientId,
                    redirect_uri: `https://${this.config.environment}.wwportal.ch`,
                    response_type: "code",
                    scope: this.authStore.scopes,
                    nonce: nonce,
                    code_challenge: loginCodeChallenge,
                    code_challenge_method: "S256",
                    state: state
                })
            }
        );

        if (generateRequestIdResponse.ok) {
            const generateRequestIdResponseBody = await generateRequestIdResponse.json();
            return generateRequestIdResponseBody;
        } else {
            return await this.handleLoginErrorResponse(generateRequestIdResponse);
        }
    }

    async loginUserToCidaas(username: string, password: string, requestId: string): Promise<ILoginUserResponse> {
        info(`loginUserToCidaas starts: ${username}`);
        const loginUserResponse = await fetch(`${this.config.cidaasBaseUrl}${this.config.cidaasLoginUserRessource}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
            },
            body: new URLSearchParams({
                username: username,
                password: password,
                requestId: requestId
            })
        });

        if (loginUserResponse.ok) {
            const loginUserResponseBody = await loginUserResponse.json();
            return loginUserResponseBody;
        } else {
            return await this.handleLoginErrorResponse(loginUserResponse);
        }
    }

    async getAccessToken(code: string, loginCodeVerifier: string): Promise<IGetTokenResponse> {
        info(`getAccessToken starts: ${code}`);
        const getAccessTokenResponse = await fetch(
            `${this.config.cidaasBaseUrl}${this.config.cidaasGetTokenRessource}`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
                },
                body: new URLSearchParams({
                    grant_type: "authorization_code",
                    client_id: this.config.wwEasyContactPasswordClientId,
                    redirect_uri: `https://${this.config.environment}.wwportal.ch`,
                    code: code,
                    code_verifier: loginCodeVerifier
                })
            }
        );

        if (getAccessTokenResponse.ok) {
            const getAccessTokenResponseBody = await getAccessTokenResponse.json();
            return getAccessTokenResponseBody;
        } else {
            return await this.handleLoginErrorResponse(getAccessTokenResponse);
        }
    }

    async login(username: string, password: string): Promise<boolean> {
        info(`login starts: ${username}`);
        this.authStore.setLogin(true);
        let getAccessTokenResponse: IGetTokenResponse | undefined = undefined;

        const loginCodeVerifier = uuidv4();

        const generateRequestIdResponse: IGenerateRequestIdResponse = await this.generateRequestId(loginCodeVerifier);

        const loginUserResponse: ILoginUserResponse = await this.loginUserToCidaas(
            username,
            password,
            generateRequestIdResponse.data.requestId
        );

        getAccessTokenResponse = await this.getAccessToken(loginUserResponse.data.code, loginCodeVerifier);

        if (getAccessTokenResponse) {
            const user: User = new User();
            await user.init(getAccessTokenResponse.access_token);

            this.authStore.addUser(
                user,
                getAccessTokenResponse.access_token,
                getAccessTokenResponse.refresh_token,
                getAccessTokenResponse.expires_in
            );

            info(`login successful: ${username}`);
            this.authStore.setLogin(false);

            if (!user.hasECManagerRole) {
                return Promise.reject(
                    new AuthError(
                        `Benutzer hat keine Berechtigung mit der Rolle "${getRoleLabel(Role.ECMANAGER)}".`,
                        "login"
                    )
                );
            } else if (user.availableRoles.length === 0) {
                return Promise.reject(
                    new AuthError(
                        `Benutzer hat keine zur Domäne passende Berechtigung mit der Rolle "${getRoleLabel(
                            Role.ECMANAGER
                        )}".`,
                        "login"
                    )
                );
            }

            return Promise.resolve(true);
        } else {
            info(`login unsuccessful: ${username}`);
            this.authStore.setLogin(false);

            return Promise.reject(new AuthError("Error during login", "login"));
        }
    }

    async relogin(token: string | undefined): Promise<boolean> {
        if (token) {
            if (!this.authStore.relogin) {
                info("relogin start");
                this.authStore.setRelogin(true);
                try {
                    await this.refresh(token);
                    return Promise.resolve(true);
                } catch (error) {
                    info(`relogin unsuccessful: ${error}`);
                    return Promise.reject(new AuthError(`relogin unsuccessful: ${error}`, "relogin"));
                } finally {
                    this.authStore.setRelogin(false);
                }
            }
            info("relogin already in progress");
            return Promise.resolve(false);
        }
        info("relogin omitted because no token available");
        return Promise.resolve(false);
    }

    async refresh(refreshToken: string | undefined): Promise<boolean> {
        if (refreshToken) {
            info("token refresh starts");

            const response = await fetch(`${this.config.cidaasBaseUrl}${this.config.cidaasGetTokenRessource}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
                },
                body: new URLSearchParams({
                    grant_type: "refresh_token",
                    refresh_token: refreshToken,
                    response_type: "code",
                    client_id: this.config.wwEasyContactPasswordClientId,
                    scope: this.authStore.scopes
                })
            });

            if (response.ok) {
                const token = await response.json();
                const user: User = new User();

                if (this.authStore.user) {
                    this.authStore.addUser(
                        this.authStore.user,
                        token.access_token,
                        token.refresh_token,
                        token.expires_in
                    );
                } else {
                    await user.init(token.access_token);
                    this.authStore.addUser(user, token.access_token, token.refresh_token, token.expires_in);
                }

                info("token refresh successful");
                return Promise.resolve(true);
            } else {
                info(`"token refresh not successful --> ${response.status}: ${response.statusText}`);
                this.authStore.setRefreshToken(undefined);
                return Promise.reject(
                    new AuthError(
                        `token refresh not successful --> ${response.status}: ${response.statusText}`,
                        "refresh"
                    )
                );
            }
        }
        info("refresh omitted because no refresh-token available");
        return Promise.resolve(false);
    }

    async logout(): Promise<boolean> {
        info("logout starts");

        this.authStore.setLogout(true);

        if (this.authStore.user || this.authStore.token) {
            const username = this.authStore.user?.username;
            if (this.authStore.token) {
                const response = await fetch(
                    `${this.config.cidaasBaseUrl}${this.config.cidaasEndSessionRessource}?access_token_hint=${this.authStore.token}`,
                    {
                        method: "POST"
                    }
                );

                info(`logout response: ${response.status}: ${response.statusText}`);

                if (response.ok) {
                    info(`logout token revocation successful: ${username}`);
                } else {
                    info(`logout token revocation not successful: ${username}`);
                    info(`--> ${response.status}: ${response.statusText}`);
                }
            } else {
                info("revocation omitted because no token available");
            }
            this.authStore.removeUser();
            info(`logout successful: ${username}`);
            this.authStore.setLogout(false);
            return Promise.resolve(true);
        } else {
            info("logout omitted because no user or token available");
            this.authStore.setLogout(false);
            return Promise.resolve(false);
        }
    }
}
